/**
 * pricing-table-checkout.js
 *
 * Native Shopify AJAX checkout handler for the SS Pricing Table section.
 *
 * Replaces Rebuy smart-link URLs. When a pricing table CTA has
 * [data-native-checkout] set, clicking it will:
 *   1. Preconnect to checkout.shopify.com on first hover/focus
 *   2. Clear the current cart
 *   3. Add the configured bundle items (with optional selling plans)
 *   3b. Wait 300ms, read cart, and remove any items added by Rebuy's
 *       threshold GWP (which has no Shopify discount at native checkout)
 *   4. Redirect via /discount/CODE?redirect=/checkout (applies discount
 *      server-side and lands the user directly at Shopify checkout)
 *
 * Falls back to the button's href if JS is disabled or if any step fails.
 *
 * Data attributes (set by ss-pricing-table-3-mod.liquid):
 *   data-native-checkout  — comma-separated items: VARIANT_ID:QTY or
 *                           VARIANT_ID:QTY:SELLING_PLAN_ID
 *   data-checkout-discount — discount code (optional)
 */

(function () {
  'use strict';

  /**
   * Parse the data-native-checkout string into a cart items array.
   * @param {string} str  e.g. "49404263629100:1,55198521196920:1,55490387935608:1:712772944248"
   * @returns {Array}
   */
  function parseItems(str) {
    var items = [];
    str.split(',').forEach(function (part) {
      var segs = part.trim().split(':');
      if (segs.length < 2) return;
      var item = {
        id: parseInt(segs[0], 10),
        quantity: parseInt(segs[1], 10) || 1
      };
      if (segs[2] && segs[2] !== '0' && segs[2] !== '') {
        item.selling_plan = parseInt(segs[2], 10);
      }
      if (!isNaN(item.id) && item.id > 0) items.push(item);
    });
    return items;
  }

  /** Warm up the connection to Shopify checkout before the user clicks. */
  function preconnectCheckout() {
    if (document.querySelector('link[rel="preconnect"][href*="checkout.shopify.com"]')) return;
    var link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://checkout.shopify.com';
    document.head.appendChild(link);
  }

  /** Set button into loading state with immediate text feedback (accessible). */
  function setLoading(btn) {
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-disabled', 'true');
    btn.style.opacity = '0.6';
    btn.style.pointerEvents = 'none';
    btn.setAttribute('data-original-text', btn.textContent.trim());
    btn.textContent = 'Preparing checkout…';
  }

  /** Restore button from loading state (used on error only). */
  function clearLoading(btn) {
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('aria-disabled');
    btn.style.opacity = '';
    btn.style.pointerEvents = '';
    var original = btn.getAttribute('data-original-text');
    if (original) btn.textContent = original;
    btn.removeAttribute('data-original-text');
  }

  /** Attach click and hover handlers to a single CTA element. */
  function attachHandler(btn) {
    // Preconnect on first hover or focus — warms the TCP connection to
    // checkout.shopify.com before the user has even clicked, saving ~200–400ms.
    btn.addEventListener('mouseenter', preconnectCheckout, { once: true });
    btn.addEventListener('focus', preconnectCheckout, { once: true });

    btn.addEventListener('click', function (e) {
      e.preventDefault();

      var itemsStr = btn.getAttribute('data-native-checkout');
      var discount = btn.getAttribute('data-checkout-discount') || '';
      var fallback = btn.getAttribute('href') || '/cart';

      var items = parseItems(itemsStr);
      if (!items.length) {
        window.location = fallback;
        return;
      }

      setLoading(btn);

      // Suppress Rebuy Smart Cart flyout — it listens for cart mutations and
      // would open the drawer before our redirect fires. Hidden via CSS; no
      // need to restore since we navigate away immediately after.
      var suppress = document.createElement('style');
      suppress.textContent = '.rebuy-cart__flyout { display: none !important; }';
      document.head.appendChild(suppress);

      // Step 1 — clear existing cart
      fetch('/cart/clear.js', { method: 'POST' })
        .then(function () {
          // Step 2 — add bundle items
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          });
        })
        .then(function (response) {
          if (!response.ok) throw new Error('cart/add failed: ' + response.status);
          return response.json();
        })
        .then(function () {
          // Rebuy monitors cart mutations globally and may inject a threshold-based
          // free-gift item (e.g. spend-£60-get-free-gift) within ~200ms of our
          // cart/add.js call. That gift has no Shopify discount at native checkout,
          // so customers would see it at full price. We pause 300ms for Rebuy to
          // fire, then sweep the cart and remove any line items we did not
          // intentionally add before redirecting.
          return new Promise(function (resolve) { setTimeout(resolve, 300); });
        })
        .then(function () {
          // Step 3 — read live cart state
          return fetch('/cart.js');
        })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          // Step 4 — sweep out any unexpected items (Rebuy GWP, etc.)
          var expectedIds = items.map(function (i) { return i.id; });
          var updates = {};
          cart.items.forEach(function (lineItem) {
            if (expectedIds.indexOf(lineItem.variant_id) === -1) {
              updates[lineItem.variant_id] = 0;
            }
          });
          if (Object.keys(updates).length === 0) {
            return; // cart is clean — skip the extra round-trip
          }
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
        })
        .then(function () {
          // Step 5 — redirect. No cart:refresh dispatch needed since we're
          // leaving the page immediately.
          window.location = discount
            ? '/discount/' + encodeURIComponent(discount) + '?redirect=/checkout'
            : '/checkout';
        })
        .catch(function (err) {
          console.error('[pricing-table-checkout]', err);
          clearLoading(btn);
          // Graceful degradation — fall back to the original button URL
          window.location = fallback;
        });
    });
  }

  // Initialise on DOM ready (script is deferred so DOM is already parsed,
  // but DOMContentLoaded guards against edge-case async injection).
  function init() {
    document.querySelectorAll('[data-native-checkout]').forEach(attachHandler);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
